import { Button } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import {
    createFieldCheckbox,
    createFieldDate,
    createFieldDatetime,
    createFieldInput,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTemplateBuilder,
    createFieldTextArea
} from "../../../../components/input/template-builder/template-builder.logic";
import {
    createColumnBoolean,
    createColumnDatetime,
    createColumnInput,
    createColumnSelect
} from '../../../../components/table/c-table.logic';
import i18n from "../../../../i18n";
import store from "../../../../store";
import { conditionFormChecker, putField } from "../../../../utils/form";
import { has } from "../../../../utils/menu-context";
import { onClickReport } from "../../../../utils/page";
import { addRules, clearRules, required } from "../../../../utils/validate";
import { callCountry } from "../../master-mgt/country/country.logic";
import { callDepartment } from "../../master-mgt/department-responsible/department-responsible.logic";
import { openModalReportList } from "../../report/report.logic";
import { callConfirmStatus } from "../company/company.section";
import { createField as createFieldCompanyDocRejectProduct } from "./company-doc-reject-product.logic";
import { callProductType } from '../company/company.logic';

const CardBorder = defineAsyncComponent(() => import("../../../../components/card/CardBorder"));
const TemplateBuilder = defineAsyncComponent(() => import("../../../../components/input/template-builder/TemplateBuilder"));
const CompanyDocRejectProductList = defineAsyncComponent(() => import("./CompanyDocRejectProductList"));
const AttachmentList = defineAsyncComponent(() => import("../../attachment/AttachmentList"));
const SamplingProductList = defineAsyncComponent(() => import("./SamplingProductList"));
const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const callCompanyDocRejectType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_DOC_REJECT_TYPE", value: searchQuery });
export const callCompanyDocRejectStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_DOC_REJECT_STATUS", value: searchQuery });
export const callCertificateRequestByHcNo = ({ searchQuery, companyId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompany.id", type: "UUID", operator: "=", value: companyId },
        { name: "refRequestStatus.key", type: "String", operator: "=", value: "Printed" }
    ];
    if (searchQuery) {
        where.push({ name: "certificateNo", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/certificateRequest/search", {
            criteria: {
                select: ["*"],
                page: 0,
                size: 1000000,
                where,
                sort: []
            }
        })
        .then(page => page.data);
};
export const callSamplingProductBySamplingNo = ({ searchQuery, companyId, data }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompanyProduct.refCompany.id", type: "UUID", operator: "=", value: companyId },
        { name: "refStatus.key", type: "String", operator: "=", value: "Send Result" },
        { name: "refAcceptanceStatus.key", type: "String", operator: "=", value: "Reject" },
        { name: "id", type: "UUID", isNot: true, operator: "in", value: data.allCompanyDocRejectProducts}

];
    if (searchQuery) {
        where.push({ name: "samplingNo", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/samplingProduct/search", {
            criteria: {
                select: ["*"],
                page: 0,
                size: 1000000,
                where,
                sort: []
            }
        })
        .then(page => page.data);
};
export const callCertificateRequestById = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/certificateRequest/search", {
            criteria: {
                select: ["*", "refExportCountry.*", "refCompanyCert.refExportCountry.*"],
                page: 0,
                size: 1000000,
                where: [
                    { name: "isActive", type: "Boolean", operator: "=", value: true },
                    { name: "id", type: "UUID", operator: "=", value: id }
                ],
                sort: [],
                children: [
                    {
                        name: "certificateProducts",
                        select: ["*", "refCompanyProduct.**", "refSamplingProduct.refCompanyProduct.**"],
                        page: 0,
                        size: 1000000,
                        sort: []
                    }
                ]
            }
        })
        .then(page => page.data?.[0]);
};
export const callSamplingProductById = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/samplingProduct/search", {
            criteria: {
                select: [
                    "*",
                    "refCompanyProduct.**",
                    //"refCountry1.*",
                    //"refCountry2.*",
                    "refSamplingRequest.*",
                    "refSamplingRequest.refCompany.id",
                    "refSamplingRequest.refSamplingDepartment.*",
                    "refExportAmountUnit.*"
                ],
                page: 0,
                size: 1000000,
                where: [
                    { name: "isActive", type: "Boolean", operator: "=", value: true },
                    { name: "id", type: "UUID", operator: "=", value: id }
                ],
                sort: [],
                children: [
                    {
                        name: "analysiss",
                        select: ["*", "refLabType.*", "refLabResultStatus.*", "refTransferFrom.*"],
                        page: 0,
                        size: 1000000,
                        where: [
                            { name: "refTransferFrom.id", type: "UUID", operator: "=", value: null },
                            { name: "refLabResultStatus.key", type: "String", operator: "=", value: "Reject" }
                        ],
                        sort: [],
                        children: [
                            {
                                name: "topics",
                                select: ["*", "refLabTopic.*", "refTopicAnalysisStatus.*"],
                                page: 0,
                                size: 1000000,
                                where: [],
                                sort: []
                            }
                        ]
                    }
                ]
            }
        })
        .then(page => page.data?.[0]);
};
export const callCompanyDocRejectReview = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_DOC_REJECT_REVIEW", value: searchQuery });
export const callCompanyByNameTh = ({ searchQuery, departmentId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refStatus.key", type: "String", operator: "in", value: ["Approve", "Suspend"] },
        //{ name: "refThNoStatus.key", type: "String", operator: "=", value: "A" },
        { name: "isTmp", type: "Boolean", operator: "=", value: false }
    ];
    if (departmentId) {
        where.push({ name: "refDepartment.id", type: "UUID", operator: "=", value: departmentId });
    }
    if (searchQuery) {
        where.push({ name: "nameTh", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/company/search", {
            criteria: {
                select: ["*"],
                page: 0,
                size: 1000000,
                where,
                sort: [],
                children: [{ name: "userCompanies", select: ["id"], page: 0, size: 1, where: [], sort: [{ name: "createDate", type: "ASC" }] }]
            }
        })
        .then(page => page.data);
};
export const callCompanyById = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/company/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1000000,
                where: [
                    { name: "isActive", type: "Boolean", operator: "=", value: true },
                    { name: "id", type: "UUID", operator: "=", value: id }
                ]
            }
        })
        .then(page => page.data);
};
export const callCompanyDocRejectProductByCompanyProductId = ({ companyProductId, searchQuery }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompanyProduct.isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompanyProduct.id", type: "UUID", operator: "=", value: companyProductId },
        { name: "refCompanyDocReject.isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompanyDocReject.refStatus.key", type: "String", operator: "=", value: "Plan" },
        { name: "refCompanyDocReject.refCompanyDocRejectReview.key", type: "String", operator: "=", value: "Efficient" },
        { name: "refCompanyDocReject.reviewSpecialDueDatetime", type: "Datetime", operator: "!=", value: null },
        { name: "refCompanyDocReject.reviewSpecialCanceledDatetime", type: "Datetime", operator: "=", value: null }
    ];
    if (searchQuery) {
        where.push({ name: "refCompanyDocReject.docNo", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/companyDocRejectProduct/search", {
            criteria: {
                select: ["*", "refCompanyDocReject.*", "refCompanyDocReject.refCompanyDocRejectType.*"],
                page: 0,
                size: 1000000,
                where,
                children: [
                    {
                        name: "topics",
                        select: ["*", "refTopic.*", "refTopic.refLabType.*"],
                        page: 0,
                        size: 1000000,
                        where: [],
                        sort: [{ name: "createDate", type: "ASC" }]
                    }
                ]
            }
        })
        .then(page => page.data);
};

export const createColumnsForInvestigation = ({ props }) => {
    let defaultCompanyDepartment = null;
    if (!has({ menuContext: props?.menuContext, key: "forCompany" })) {
        if (!has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })) {
            defaultCompanyDepartment =
                store.state.resource.auth.info?.user?.refDepartment?.refParent || store.state.resource.auth.info?.user?.refDepartment;
        }
    }
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.docNo"), dataIndex: "docNo", align: "center" }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.receiveDatetime"), dataIndex: "receiveDatetime" }),
        createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompany.thNo", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompany.nameTh" }),
        // createColumnInput({ title: () => i18n.global.t("model.company.refMainProductType"), dataIndex: "refCompany.refMainProductType.value" }),
        createColumnSelect({
            visible: () => false,
            title: () => i18n.global.t("model.company.refMainProductType"),
            dataIndex: "refCompany.refMainProductType.value",
            inputProp: () => ({ list: callProductType, optLabel: "value" }),
            search: { name: "refCompany.refMainProductType.id" }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.certificateRequest.certificateNo"),
            dataIndex: "refCompanyCert.certificateNo",
            align: "center"
        }),
        // createColumnInput({ title: () => i18n.global.t("model.companyDocReject.problem"), dataIndex: "problem", width: 200 }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemMicroReport"), dataIndex: "isProblemMicro", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.microProblem"), dataIndex: "microProblem", width: 200 }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemChemiReport"), dataIndex: "isProblemChemi", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.chemiProblem"), dataIndex: "chemiProblem", width: 200 }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemPhysicReport"), dataIndex: "isProblemPhysic", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.physicProblem"), dataIndex: "physicProblem", width: 200 }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.companyDatetime"), dataIndex: "companyDatetime" }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyDocReject.refStatus"),
            dataIndex: "refStatus.value",
            customRender: ({ record }) => <div>{record.refStatus?.value}</div>,
            inputProp: () => ({ list: callCompanyDocRejectStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t('model.companyDocReject.refCountry'),
            dataIndex: 'refCountry.nameEn',
            inputProp: () => ({
                list: callCountry,
                renderLabel: ({ item }) => <div>{item.nameTh} ({item.nameEn})</div>,
            }),
            align: "center",
            search: {
                name: "refCountry.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.company.refDepartment"),
            dataIndex: "refCompany.refDepartment.nameTh",
            customRender: ({ record }) => <div>{record.refCompany?.refDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartment, optLabel: "nameTh" }),
            search: {
                name: "refCompany.refDepartment.id",
                visible: () => has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" }),
                field: { searchValue: defaultCompanyDepartment }
            }
        }),
    ];
    return columns;
};

export const createColumnsForFollowUp = ({ props }) => {
    let defaultCompanyDepartment = null;
    if (!has({ menuContext: props?.menuContext, key: "forCompany" })) {
        if (!has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })) {
            defaultCompanyDepartment =
                store.state.resource.auth.info?.user?.refDepartment?.refParent || store.state.resource.auth.info?.user?.refDepartment;
        }
    }
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.docNo"), dataIndex: "docNo", align: "center" }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.receiveDatetime"), dataIndex: "receiveDatetime" }),
        createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompany.thNo", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompany.nameTh" }),
        // createColumnInput({ title: () => i18n.global.t("model.company.refMainProductType"), dataIndex: "refCompany.refMainProductType.value" }),

        createColumnSelect({
            visible: () => false,
            title: () => i18n.global.t("model.company.refMainProductType"),
            dataIndex: "refCompany.refMainProductType.value",
            inputProp: () => ({ list: callProductType, optLabel: "value" }),
            search: { name: "refCompany.refMainProductType.id" }
        }),
        // createColumnInput({ title: () => i18n.global.t("model.companyDocReject.problem"), dataIndex: "problem", width: 200 }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.companyDatetime"), dataIndex: "companyDatetime" }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemMicroReport"), dataIndex: "isProblemMicro", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.microProblem"), dataIndex: "microProblem", width: 200 }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemChemiReport"), dataIndex: "isProblemChemi", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.chemiProblem"), dataIndex: "chemiProblem", width: 200 }),
        createColumnBoolean({ title: () => i18n.global.t("views.company.companyDocReject.isProblemPhysicReport"), dataIndex: "isProblemPhysic", width: 150 }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.physicProblem"), dataIndex: "physicProblem", width: 200 }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyDocReject.refStatus"),
            dataIndex: "refStatus.value",
            customRender: ({ record }) => <div>{record.refStatus?.value}</div>,
            inputProp: () => ({ list: callCompanyDocRejectStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.company.refDepartment"),
            dataIndex: "refCompany.refDepartment.nameTh",
            customRender: ({ record }) => <div>{record.refCompany?.refDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartment, optLabel: "nameTh" }),
            search: {
                name: "refCompany.refDepartment.id",
                visible: () => has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" }),
                field: { searchValue: defaultCompanyDepartment }
            }
        }),
    ];
    return columns;
};

export const createColumnsForCompanyDocReject = () => {
    const columns = [
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.companyDatetime"), dataIndex: "companyDatetime" }),
        createColumnInput({ title: () => i18n.global.t("model.companyDocReject.docNo"), dataIndex: "docNo", align: "center" }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyDocReject.refCompanyDocRejectType"),
            dataIndex: "refCompanyDocRejectType.value",
            customRender: ({ record }) => <div>{record.refCompanyDocRejectType?.value}</div>,
            inputProp: () => ({ list: callCompanyDocRejectType, optLabel: "value" }),
            align: "center",
            search: { name: "refCompanyDocRejectType.id" }
        }),
        createColumnInput({ title: () => i18n.global.t("views.company.companyDocReject.officer"), dataIndex: "refCreateBy.fullName" }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyDocReject.dueDatetime"), dataIndex: "dueDatetime" }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyCalendar.refStatus"),
            dataIndex: "refStatus.value",
            customRender: ({ record }) => <div>{record.refStatus?.value}</div>,
            inputProp: () => ({ list: callCompanyDocRejectStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        })
    ];
    return columns;
};

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyDocReject" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t(`views.company.${routerPrefix.substring(0, routerPrefix.length - 1)}.title`)
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "CompanyDocReject" } })
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldCompanyDocReject({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Attachment" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.company.companyDocReject.attachment.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "Attachment" } })
                    );
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            parent: model,
                            group: "Attachment"
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "Attachment" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldSendPlan({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                            <AttachmentList {...componentProp}></AttachmentList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyDocRejectProduct" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: "companyDocRejectProducts",
                            visibleCompanyDocReject: false
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "CompanyDocRejectProduct" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return () => (
                        <CardBorder title={i18n.global.t(`model.samplingRequest.samplingProducts`)}>
                            <CompanyDocRejectProductList {...componentProp}></CompanyDocRejectProductList>
                            {/*
                            <div class="mt-2 text-center">
                                <Button
                                    size="small"
                                    class="btn-primary"
                                    onClick={() =>
                                        openModalIFrameFormUserCompany({
                                            userCompanyId: model.refCompany.userCompanies[0].id,
                                            query:
                                                "&hideChangeLog=true&hideAll=true&visibleCompanyProduct=true&canProductSuspendedSampling=true&canProductSuspendedHc=true"
                                        })
                                    }
                                >
                                    {i18n.global.t("views.company.companyDocReject.manageProduct.title")}
                                </Button>
                            </div>
                            */}
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "ConsiderPlan" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => (
                    <CardBorder title={i18n.global.t(`views.company.companyDocReject.considerPlan.title`)}>
                        <TemplateBuilder
                            rowProp={data.rowProp}
                            useForm={data.useForm}
                            fields={createFieldConsiderPlan({ props, data, key, isLoading })}
                            skeleton={isLoading.value}
                        ></TemplateBuilder>
                    </CardBorder>
                )
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Special" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => (
                    <CardBorder title={i18n.global.t(`views.company.companyDocReject.special.title`)}>
                        <TemplateBuilder
                            rowProp={data.rowProp}
                            useForm={data.useForm}
                            fields={createFieldConsider({ props, data, key, isLoading })}
                            skeleton={isLoading.value}
                        ></TemplateBuilder>
                    </CardBorder>
                )
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "FollowupRejectLab" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const companyDocRejectProductIndex = model.companyDocRejectProducts.findIndex(d => d.isDelete !== true);
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.company.companyDocReject.followupRejectLab.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "FollowupRejectLab" } })
                    );
                    return () => (
                        <CardBorder {...cardProp}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldCompanyDocRejectProduct({
                                    props,
                                    data,
                                    key: `${prefix}companyDocRejectProducts.${companyDocRejectProductIndex}`
                                })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "SamplingProductList" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const flatSamplingProducts = model.companyDocRejectProducts.flatMap(d => d.samplingProducts);
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: { ...data, useForm: { ...useForm, modelRef: { ...modelRef, flatSamplingProducts } } },
                            itemsKey: "flatSamplingProducts"
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "SamplingProductList" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return () => (
                        <CardBorder title={i18n.global.t(`views.company.companyDocReject.samplingProduct.title`)}>
                            <SamplingProductList {...componentProp}></SamplingProductList>
                        </CardBorder>
                    );
                }
            }
        ]
    });
};

export const createFieldCompanyDocReject = ({ props, data, key, isLoading }) => {
    const { useForm } = data;
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: createFieldCompanyDocReject1({ props, data, key, isLoading }),
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: createFieldCompanyDocReject2({ props, data, key, isLoading }),
                    skeleton: isLoading.value
                })
            })
        ]
    });
};

export const createFieldCompanyDocReject1 = ({ props, data, key, isLoading }) => {
    const { viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}receiveDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.receiveDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.receiveDatetime,
                            "onUpdate:value": value => (model.receiveDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompany`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.refCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompany,
                            "onUpdate:value": value => {
                                model.refCompany = value;
                                if (routerPrefix.includes("investigation")) {
                                    model.refCompanyCert = null;
                                    model.refCountry = null;
                                    model.exportName = null;
                                    model.exportAddress = null;
                                    model.productName = null;
                                    model.productDesc = null;
                                } else if (routerPrefix.includes("follow-up")) {
                                    clearRules({ useForm, key: `${prefix}companyDocRejectProducts.0.refSamplingProduct` });
                                    model.isProblemMicro = false;
                                    model.microProblem = null;
                                    model.isProblemChemi = false;
                                    model.chemiProblem = null;
                                    model.isProblemPhysic = false;
                                    model.physicProblem = null;
                                    model.productName = null;
                                    model.productDesc = null;
                                }
                                lodash.remove(model.companyDocRejectProducts, d => !d.id);
                                model.companyDocRejectProducts.forEach(d => (d.isDelete = true));
                                if (value) {
                                    callCompanyById({ id: value.id }).then(d => (model.refCompany = d[0]));
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callCompanyByNameTh({
                                    searchQuery,
                                    departmentId: !has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })
                                        ? store.state.resource.auth.info?.user?.refDepartment?.refParent?.id || store.state.resource.auth.info?.user?.refDepartment?.id
                                        : null
                                }),
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompanyCert`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.refCompanyCert"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompanyCert,
                            "onUpdate:value": value => {
                                model.refCompanyCert = value;
                                lodash.remove(model.companyDocRejectProducts, d => !d.id);
                                model.companyDocRejectProducts.forEach(d => (d.isDelete = true));
                                if (value) {
                                    callCertificateRequestById({ id: value.id }).then(d => {
                                        model.refCompanyCert = d;
                                        model.refCountry = d.refExportCountry;
                                        model.exportName = d.exporterName;
                                        model.exportAddress = d.exporterAddress;
                                        model.productName = d.commodityName;
                                        model.productDesc = d.certificateProducts
                                            .map(
                                                d => `${d.refSamplingProduct.samplingNo} ${d.refSamplingProduct.packageSize} ${d.exportAmount}Carton`
                                            )
                                            .join(", ");
                                        model.companyDocRejectProducts.push(
                                            ...d.certificateProducts.map(d => ({
                                                refCompanyProduct: d.refCompanyProduct || d.refSamplingProduct.refCompanyProduct,
                                                refSamplingProduct: d.refSamplingProduct,
                                                analysiss: [],
                                                topics: []
                                            }))
                                        );
                                    });
                                } else {
                                    model.refCountry = null;
                                    model.exportName = null;
                                    model.exportAddress = null;
                                    model.productName = null;
                                    model.productDesc = null;
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callCertificateRequestByHcNo({ searchQuery, companyId: model.refCompany?.id }),
                            optLabel: "certificateNo"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.certificateNo`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCountry`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.refCountry"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCountry,
                            "onUpdate:value": value => (model.refCountry = value),
                            disabled: viewOnly || model.refCompanyCert,
                            list: callCountry,
                            optLabel: "nameEn"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyDocRejectProducts.0.refSamplingProduct`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocRejectProduct.refSamplingProduct"),
                inputProp: ({ field }) => {
                    let companyDocRejectProduct = model.companyDocRejectProducts.find(d => d.isDelete !== true);
                    if (!companyDocRejectProduct && model.refCompany && model.refCompanyDocRejectType?.key === "2") {
                        model.companyDocRejectProducts.unshift((companyDocRejectProduct = { analysiss: [], topics: [] }));
                        addRules({ useForm, modelRef, key: `${prefix}companyDocRejectProducts.0.refSamplingProduct`, rules: [required] });
                    }
                    return lodash.merge(
                        {
                            value: companyDocRejectProduct?.refSamplingProduct,
                            "onUpdate:value": value => {
                                companyDocRejectProduct.refSamplingProduct = value;
                                companyDocRejectProduct.isSuspendedSampling = false;
                                companyDocRejectProduct.isSuspendedHc = false;
                                companyDocRejectProduct.isSave = true;
                                lodash.remove(model.companyDocRejectProducts, d => !d.id && d._id !== companyDocRejectProduct._id);
                                model.companyDocRejectProducts.filter(d => d.id).forEach(d => (d.isDelete = true));
                                if (value) {
                                    callSamplingProductById({ id: value.id }).then(samplingProduct =>
                                        onChangeSamplingProduct({ companyDocReject: model, samplingProduct })
                                    );
                                } else {
                                    companyDocRejectProduct.refCompanyProduct = null;
                                    model.isProblemMicro = false;
                                    model.microProblem = null;
                                    model.isProblemChemi = false;
                                    model.chemiProblem = null;
                                    model.isProblemPhysic = false;
                                    model.physicProblem = null;
                                    model.productName = null;
                                    model.productDesc = null;
                                }
                            },
                            disabled: viewOnly || !model.refCompany?.id,
                            list: ({ searchQuery }) => callSamplingProductBySamplingNo({ searchQuery, companyId: model.refCompany?.id, data }),
                            optLabel: "samplingNo",
                            selectWithIsSave: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: {
                    getValue: ({ record }) => {
                        if (lodash.get(record, `${prefix}companyDocRejectProducts.0.0.refSamplingProduct`) === null) return null;
                        return lodash.get(record, `${prefix}companyDocRejectProducts.0.0.refSamplingProduct.samplingNo`);
                    }
                }
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: { offset: 10, span: 14 } }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: createFieldCompanyDocReject1_1({ props, data, key, isLoading }),
                    skeleton: isLoading.value
                })
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}downloadRejectForm`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("views.company.companyDocReject.rejectForm.download"),
                inputRender: () => {
                    return (
                        <Button
                            class="btn-danger m-2"
                            size="small"
                            onClick={() => {
                                const reports = model.companyDocRejectProducts
                                    .filter(d => d.isDelete !== true)
                                    .flatMap(d => d.refSamplingProduct?.analysiss || [])
                                    .map(d => {
                                        return {
                                            visible: () => true,
                                            fileName: `Reject Form ${d.refLabType.valueEn}.pdf`,
                                            callReport: () =>
                                                onClickReport({
                                                    fileName: `Reject Form ${d.refLabType.valueEn}.pdf`,
                                                    callReport: () => {
                                                        const {
                                                            createConditionReport
                                                        } = require("../../lab/sampling-product-analysis/analysis.report");
                                                        const {
                                                            callJasperReportByKey
                                                        } = require("../../lab/sampling-product-analysis/analysis.logic");
                                                        const report = createConditionReport().rejectionForm({ modelRef: d });
                                                        return callJasperReportByKey({ data: { ...report, attribute: { id: d.id } } });
                                                    }
                                                })
                                        };
                                    });
                                openModalReportList({ props, data: { reports } });
                            }}
                        >
                            {" "}
                            <span>{i18n.global.t("model.samplingProductAnalysis.buttons.rejectReport")}</span>
                            <CIcon file={"report.svg"}></CIcon>
                        </Button>
                    );
                }
            },
            /*
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}problem`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.problem"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.problem,
                            "onUpdate:value": value => (model.problem = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            */
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}productName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.productName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.productName,
                            "onUpdate:value": value => (model.productName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}productDesc`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.productDesc"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.productDesc,
                            "onUpdate:value": value => (model.productDesc = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyDocRejectProducts.0.refSamplingProduct.refSamplingRequest.refSamplingDepartment.nameTh`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.samplingRequest.refSamplingDepartment"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.companyDocRejectProducts?.[0]?.refSamplingProduct?.refSamplingRequest?.refSamplingDepartment?.nameTh,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: {
                    getValue: ({ record }) => {
                        if (
                            lodash.get(
                                record,
                                `${prefix}companyDocRejectProducts.0.0.refSamplingProduct.refSamplingRequest.refSamplingDepartment`
                            ) === null
                        )
                            return null;
                        return lodash.get(
                            record,
                            `${prefix}companyDocRejectProducts.0.0.refSamplingProduct.refSamplingRequest.refSamplingDepartment.nameTh`
                        );
                    }
                }
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompany.refStatus`,
                colProp: () => ({ xs: 24, md: { offset: 10 } }),
                formItemProp: () => ({ wrapperCol: { xs: 24, md: 14 } }),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            checked: model.refCompany?.refStatus?.key === "Suspend" ? true : false,
                            "onUpdate:checked": checked => {
                                model.refCompany.isSave = true;
                                if (checked) {
                                    callConfirmStatus({}).then(statuses => (model.refCompany.refStatus = statuses.find(d => d.key === "Suspend")));
                                    model.refCompany.companySuspendedDatetime = moment();
                                    model.refCompany.suspendedRemark = model.refCompanyDocRejectType.value;
                                } else {
                                    callConfirmStatus({}).then(statuses => (model.refCompany.refStatus = statuses.find(d => d.key === "Approve")));
                                    model.refCompany.companySuspendedDatetime = null;
                                    model.refCompany.suspendedRemark = null;
                                }
                            },
                            disabled: viewOnly,
                            text: () => i18n.global.t("model.companyDocReject.isSuspended")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}dueDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.dueDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.dueDatetime,
                            "onUpdate:value": value => (model.dueDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldCompanyDocReject1_1 = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isProblemMicro`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            checked: model.isProblemMicro,
                            "onUpdate:checked": checked => {
                                model.isProblemMicro = checked;
                                if (!checked) {
                                    model.microProblem = null;
                                }
                            },
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isProblemMicro")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}microProblem`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.companyDocReject.microProblem"),
                labelRender: () => "",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.microProblem,
                            "onUpdate:value": value => (model.microProblem = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isProblemChemi`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            checked: model.isProblemChemi,
                            "onUpdate:checked": checked => {
                                model.isProblemChemi = checked;
                                if (!checked) {
                                    model.chemiProblem = null;
                                }
                            },
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isProblemChemi")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}chemiProblem`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.companyDocReject.chemiProblem"),
                labelRender: () => "",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.chemiProblem,
                            "onUpdate:value": value => (model.chemiProblem = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isProblemPhysic`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            checked: model.isProblemPhysic,
                            "onUpdate:checked": checked => {
                                model.isProblemPhysic = checked;
                                if (!checked) {
                                    model.physicProblem = null;
                                }
                            },
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isProblemPhysic")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}physicProblem`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.companyDocReject.physicProblem"),
                labelRender: () => "",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.physicProblem,
                            "onUpdate:value": value => (model.physicProblem = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldCompanyDocReject2 = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}destinationCountryDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.destinationCountryDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.destinationCountryDatetime,
                            "onUpdate:value": value => (model.destinationCountryDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}exportName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.exportName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.exportName,
                            "onUpdate:value": value => (model.exportName = value),
                            disabled: viewOnly || model.refCompanyCert
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}exportAddress`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.exportAddress"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.exportAddress,
                            "onUpdate:value": value => (model.exportAddress = value),
                            disabled: viewOnly || model.refCompanyCert
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCode`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.refCode"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCode,
                            "onUpdate:value": value => (model.refCode = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyDocRejectProducts.0.refSamplingProduct.refSamplingRequest.samplingCollectDate`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.samplingRequest.samplingCollectDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.companyDocRejectProducts?.[0]?.refSamplingProduct?.refSamplingRequest?.samplingCollectDate,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: () => undefined }
            }),
            /*
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyDocRejectProducts.0.refSamplingProduct.exportCountry`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.samplingProduct.exportCountry"),
                inputProp: ({ field }) => {
                    const country1 = model.companyDocRejectProducts?.[0]?.refSamplingProduct?.refCountry1?.nameTh || "";
                    const country2 = model.companyDocRejectProducts?.[0]?.refSamplingProduct?.refCountry2?.nameTh || "";
                    return lodash.merge(
                        {
                            value: [country1, country2].filter(d => d).join(", "),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            */
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: { offset: 10, span: 14 } }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: createFieldCompanyDocReject2_1({ props, data, key, isLoading }),
                    skeleton: isLoading.value
                })
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.remark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.remark,
                            "onUpdate:value": value => (model.remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}noPlanRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 10 }, wrapperCol: { xs: 24, md: 14 } }),
                label: () => i18n.global.t("model.companyDocReject.noPlanRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.noPlanRemark,
                            "onUpdate:value": value => (model.noPlanRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldCompanyDocReject2_1 = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isAnalysisMicro`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    let isAnalysisMicro = false;
                    if (model.refCompanyDocRejectType?.key === "2") {
                        const companyDocRejectProduct = model.companyDocRejectProducts.find(d => d.isDelete !== true);
                        if (companyDocRejectProduct?.refSamplingProduct) {
                            isAnalysisMicro = companyDocRejectProduct.refSamplingProduct.isMicro;
                        }
                    }
                    return lodash.merge(
                        {
                            checked: isAnalysisMicro,
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isAnalysisMicro")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isAnalysisChemi`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    let isAnalysisChemi = false;
                    if (model.refCompanyDocRejectType?.key === "2") {
                        const companyDocRejectProduct = model.companyDocRejectProducts.find(d => d.isDelete !== true);
                        if (companyDocRejectProduct?.refSamplingProduct) {
                            isAnalysisChemi = companyDocRejectProduct.refSamplingProduct.isChemi;
                        }
                    }
                    return lodash.merge(
                        {
                            checked: isAnalysisChemi,
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isAnalysisChemi")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isAnalysisPhysic`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) => {
                    let isAnalysisPhysic = false;
                    if (model.refCompanyDocRejectType?.key === "2") {
                        const companyDocRejectProduct = model.companyDocRejectProducts.find(d => d.isDelete !== true);
                        if (companyDocRejectProduct?.refSamplingProduct) {
                            isAnalysisPhysic = companyDocRejectProduct.refSamplingProduct.isPhysic;
                        }
                    }
                    return lodash.merge(
                        {
                            checked: isAnalysisPhysic,
                            disabled: viewOnly,
                            text: () => i18n.global.t("views.company.companyDocReject.isAnalysisPhysic")
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: () => undefined }
            })
        ]
    });
};

export const createFieldSendPlan = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}planDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyDocReject.planDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.planDatetime,
                            "onUpdate:value": value => (model.planDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}planRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyDocReject.planRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.planRemark,
                            "onUpdate:value": value => (model.planRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldConsiderPlan = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompanyDocRejectReview`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("views.company.companyDocReject.considerPlan.title"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompanyDocRejectReview,
                            "onUpdate:value": value => (model.refCompanyDocRejectReview = value),
                            disabled: viewOnly,
                            list: callCompanyDocRejectReview,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}reviewDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyDocReject.reviewDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.reviewDatetime,
                            "onUpdate:value": value => (model.reviewDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}reviewRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyDocReject.reviewRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.reviewRemark,
                            "onUpdate:value": value => (model.reviewRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldConsider = ({ props, data, key, isLoading }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({
                        data,
                        fields: [
                            createFieldDatetime({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}reviewSpecialDueDatetime`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.companyDocReject.reviewSpecialDueDatetime"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.reviewSpecialDueDatetime,
                                            "onUpdate:value": value => (model.reviewSpecialDueDatetime = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            }),
                            createFieldDatetime({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}reviewSpecialCanceledDatetime`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.companyDocReject.reviewSpecialCanceledDatetime"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.reviewSpecialCanceledDatetime,
                                            "onUpdate:value": value => (model.reviewSpecialCanceledDatetime = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            })
                        ]
                    }),
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: putField({
                        data,
                        fields: [
                            createFieldTextArea({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}reviewSpecialRemark`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.companyDocReject.reviewSpecialRemark"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.reviewSpecialRemark,
                                            "onUpdate:value": value => (model.reviewSpecialRemark = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            }),
                            createFieldTextArea({
                                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                                key: `${prefix}reviewSpecialCanceledRemark`,
                                colProp: () => ({ xs: 24 }),
                                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                                label: () => i18n.global.t("model.companyDocReject.reviewSpecialCanceledRemark"),
                                inputProp: ({ field }) =>
                                    lodash.merge(
                                        {
                                            value: model.reviewSpecialCanceledRemark,
                                            "onUpdate:value": value => (model.reviewSpecialCanceledRemark = value),
                                            disabled: viewOnly
                                        },
                                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                    )
                            })
                        ]
                    }),
                    skeleton: isLoading.value
                })
            })
        ]
    });
};

export const onChangeSamplingProduct = ({ companyDocReject, samplingProduct }) => {
    let companyDocRejectProduct = companyDocReject.companyDocRejectProducts.find(d => d.isDelete !== true);
    companyDocRejectProduct.refSamplingProduct = samplingProduct;
    companyDocRejectProduct.refCompanyProduct = samplingProduct.refCompanyProduct;

    companyDocReject.isProblemMicro =
        companyDocRejectProduct.refSamplingProduct.analysiss?.filter(d => d.refLabType?.key === "labMicro" && d.refLabResultStatus?.key === "Reject")
            .length > 0;
    companyDocReject.isProblemChemi =
        companyDocRejectProduct.refSamplingProduct.analysiss?.filter(d => d.refLabType?.key === "labChemi" && d.refLabResultStatus?.key === "Reject")
            .length > 0;
    companyDocReject.isProblemPhysic =
        companyDocRejectProduct.refSamplingProduct.analysiss?.filter(d => d.refLabType?.key === "labPhysic" && d.refLabResultStatus?.key === "Reject")
            .length > 0;

    companyDocReject.microProblem = companyDocRejectProduct.refSamplingProduct.analysiss
        ?.filter(d => d.refLabType?.key === "labMicro" && !d.refTransferFrom)?.[0]
        ?.topics?.filter(d => d.refTopicAnalysisStatus?.key === "Reject" && d.isShowReport)
        ?.map(d => d.refLabTopic?.name)
        ?.join(", ");
    companyDocReject.chemiProblem = companyDocRejectProduct.refSamplingProduct.analysiss
        ?.filter(d => d.refLabType?.key === "labChemi" && !d.refTransferFrom)?.[0]
        ?.topics?.filter(d => d.refTopicAnalysisStatus?.key === "Reject" && d.isShowReport)
        ?.map(d => d.refLabTopic?.name)
        ?.join(", ");
    companyDocReject.physicProblem = companyDocRejectProduct.refSamplingProduct.analysiss
        ?.filter(d => d.refLabType?.key === "labPhysic" && !d.refTransferFrom)?.[0]
        ?.topics?.filter(d => d.refTopicAnalysisStatus?.key === "Reject" && d.isShowReport)
        ?.map(d => d.refLabTopic?.name)
        ?.join(", ");
    companyDocReject.productName = companyDocRejectProduct.refSamplingProduct.samplingNameTh;
    companyDocReject.productDesc = [
        `${i18n.global.t("model.samplingProduct.exportCountry")} ${[
            companyDocRejectProduct.refSamplingProduct.refCountry1?.nameTh,
            companyDocRejectProduct.refSamplingProduct.refCountry2?.nameTh
        ].join(", ")}`,
        companyDocRejectProduct.refSamplingProduct.packageSize
            ? `${i18n.global.t("model.samplingProduct.packageSize")} ${companyDocRejectProduct.refSamplingProduct.packageSize}`
            : null,
        companyDocRejectProduct.refSamplingProduct.exportAmount
            ? `${i18n.global.t("model.samplingProduct.exportAmount")} ${companyDocRejectProduct.refSamplingProduct.exportAmount} ${
                  companyDocRejectProduct.refSamplingProduct.refExportAmountUnit.value
              }`
            : null
    ]
        .filter(d => d)
        .join(", ");
};
